/* eslint-disable react-hooks/rules-of-hooks */
import React, { useContext, useMemo } from 'react';
import t from 'react-translate';
import _ from 'underscore';

// Hooks
import useSessionStates from 'shared/hooks/use-session-states';

// Types
import { FroalaViewMode } from 'froala/helpers/nv-froala-constants';

// Styles
import { css } from '@emotion/react';
import { gray3 } from 'styles/global_defaults/colors';

// Components
import { LiveEventSession } from 'redux/schemas/models/live-event';
import NvFroala from 'froala/components/nv-froala';
import NvIcon from 'shared/components/nv-icon';
import moment from 'moment';
import { LiveSessionsContext } from './live-session-lecture-component';
import LiveSessionCard from './live-session-card/live-session-card';

const dividerStyles = css`
  .line {
    height: 1px;
    flex-grow: 1;
    background: ${gray3};
  }
`;

const SessionsDivider = () => (
  <div css={dividerStyles} className='sessions-divider d-flex align-items-center mb-5 mt-5'>
    <div className='line' />
    <div className='text gray-3 text-regular ml-2 mr-2'>{t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.COMPLETED_SESSIONS()}</div>
    <div className='line' />
  </div>
);

const MultipleLiveSessions = () => {
  const {
    sessions,
    mainDescription,
    isEdit,
    onSaveExpectations,
    isAdmin,
    registrationLimit,
    lectureComponent,
  } = useContext(LiveSessionsContext);

  const [mainDescriptionText, setMainDescriptionText] = React.useState(mainDescription);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const LiveSession = ({ session }: { session: LiveEventSession }) => (
    <LiveSessionCard
      key={session.id}
      session={session}
    />
  );

  const {
    sessionsRegistered,
    sessionsNotRegisteredCompleted,
    sessionsRegisteredNotComplted,
    sessionsNotRegisteredNotCompleted,
    sessionsRegisteredCompleted,
    sessionsNotRegisteredNotCompletedFull,
  } = useMemo(() => {
    const registered: LiveEventSession[] = [];
    const registeredCompleted: LiveEventSession[] = [];
    const registeredNotCompleted: LiveEventSession[] = [];
    const notRegisteredCompleted: LiveEventSession[] = [];
    const notRegisteredNotCompleted: LiveEventSession[] = [];
    const notRegisteredNotCompletedFull: LiveEventSession[] = [];

    sessions.forEach((session) => {
      const {
        attendeeInfo, startTime, duration,
        timeZone: timezone, totalRegisteredUsersCount,
      } = session;
      const { ended } = useSessionStates({ startTime, duration, timezone });
      if (attendeeInfo?.register && !isAdmin) {
        registered.push(session);
        if (ended && !isEdit) {
          registeredCompleted.push(session);
        } else {
          registeredNotCompleted.push(session);
        }
      } else if (ended && !isEdit) {
        notRegisteredCompleted.push(session);
      } else if (totalRegisteredUsersCount >= registrationLimit) {
        notRegisteredNotCompletedFull.push(session);
      } else {
        notRegisteredNotCompleted.push(session);
      }
    });

    const sortByStartTime = (a: LiveEventSession, b: LiveEventSession) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf();
    // Retain the admin setup order (created order) for the completed sessions, and sort not completed ones by start time
    return {
      sessionsRegistered: registered,
      sessionsRegisteredCompleted: registeredCompleted,
      sessionsRegisteredNotComplted: registeredNotCompleted.sort(sortByStartTime),
      sessionsNotRegisteredCompleted: notRegisteredCompleted,
      sessionsNotRegisteredNotCompleted: notRegisteredNotCompleted.sort(sortByStartTime),
      sessionsNotRegisteredNotCompletedFull: notRegisteredNotCompletedFull.sort(sortByStartTime),
    };
  }, [sessions, isAdmin, isEdit, registrationLimit]);

  const showOtherSessions = sessionsRegistered.length > 0
    && sessionsRegistered.length < sessions.length
    && !isAdmin;

  return (
    <React.Fragment>
      {isEdit ? (
        <NvFroala
          preset={FroalaViewMode.AIR}
          onBlur={() => onSaveExpectations(mainDescriptionText)}
          placeholder={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.ADD_EXPECTATIONS()}
          onChange={text => setMainDescriptionText(text)}
          value={mainDescriptionText}
          isDisabled={lectureComponent?.lecturePage?.isLinked}
        />
      ) : (
        <div
          className='exercise-description'
          dangerouslySetInnerHTML={{
            __html: mainDescriptionText,
          }}
        />
      )}

      {sessionsRegisteredNotComplted?.map((session: LiveEventSession) => (
        <LiveSession session={session} key={session.id} />
      ))}
      {sessionsRegisteredCompleted?.map((session: LiveEventSession) => (
        <LiveSession session={session} key={session.id} />
      ))}
      <div className={`d-flex flex-column ${showOtherSessions ? 'mt-4 border-gray-4 border-top border-bottom py-4' : ''}`}>
        {showOtherSessions && (
          <div className='d-flex justify-content-between align-items-center px-2'>
            <div className='text-large-body font-weight-bolder'>
              {t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.OTHER_SESSIONS()}
            </div>
            <NvIcon
              icon={isExpanded ? 'arrow-up' : 'arrow-down'}
              size='smallest'
              onClick={() => setIsExpanded(!isExpanded)}
              altLabel={isExpanded ? t.COURSE_HOME.COLLAPSE() : t.COURSE_HOME.EXPAND()}
            />
          </div>
        )}
        {(isExpanded || sessionsRegistered.length === 0) && (
          <React.Fragment>
            {/* Not completed sessions */}
            {sessionsNotRegisteredNotCompleted.map((session: LiveEventSession) => (
              <LiveSession session={session} key={session.id} />
            ))}
            {sessionsNotRegisteredNotCompletedFull.map((session: LiveEventSession) => (
              <LiveSession session={session} key={session.id} />
            ))}
            {/* Ended sessions */}
            {sessionsNotRegisteredCompleted.length > 0 && <SessionsDivider />}
            {sessionsNotRegisteredCompleted.map((session: LiveEventSession) => (
              <LiveSession session={session} key={session.id} />
            ))}
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};

export default MultipleLiveSessions;
